import { useState, useEffect } from 'react'
import { styled } from '@mui/material/styles'
import Dialog from '@mui/material/Dialog'
import { NativeVideoPlayer } from 'components/VideoPlayer'
import { bytesToSize } from 'utils/format/byteToSize'
import ProgressMedia from 'components/Card/ProgressMedia'
import styles from './MediaPreviewPopup.pcss'
import SimpleLoader from 'components/SimpleLoader'
import { getFileSize, getMediaDimensions } from 'utils/file/helpers'

const cssVar = require('styles/variables')
const PREFIX = 'MediaPreviewPopup'

const classes = {
  root: `${PREFIX}-root`
}
const StyledDialog = styled(Dialog)({
  [`& .${classes.root}`]: {
    fontSize: '1.2rem',
    color: cssVar['--alert-red'],
    margin: '0 0 0 4px',

    '&:hover': {
      backgroundColor: cssVar['--pp-black-38']
    }
  }
})

export interface MediaPreviewPopupProps {
  fileUrl: string
  mediaType: 'image' | 'video'
  elementSelector: string
  onClose: () => void
}

export function MediaPreviewPopup(props: MediaPreviewPopupProps) {
  const { fileUrl, mediaType, elementSelector, onClose } = props
  const [imageLoading, setImageLoading] = useState(true)
  const [fileSize, setFileSize] = useState(0)
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 })
  const prettySize = bytesToSize(fileSize)

  useEffect(() => {
    if (!fileUrl) {
      return
    }
    const { width, height } = getMediaDimensions(mediaType === 'video' ? 'video' : 'image', elementSelector)
    setDimensions({ width, height })

    const getSize = async (url: string) => {
      const size = await getFileSize(url)
      if (size) {
        setFileSize(size)
      }
    }

    getSize(fileUrl)

  }, [fileUrl, elementSelector, mediaType])

  const stopImageLoading = () => {
    setImageLoading(false)
  }

  const fixedFooterClass = mediaType === 'video' ? styles['footer-fixed'] : ''

  return (
    <StyledDialog
      open={Boolean(fileUrl)}
      onClose={onClose}
      classes={{ paper: styles.dialog }}
      className={fixedFooterClass}
      disableEnforceFocus
    >
      {fileUrl && imageLoading && (
        <ProgressMedia
          image={fileUrl}
          loader={<SimpleLoader className={styles.loader} />}
          className={styles['loader-box']}
          onDone={stopImageLoading}
          onError={stopImageLoading}
        />
      )}
      {fileUrl && !imageLoading && (
        <div className={styles.content}>
          {mediaType === 'image' && <img src={fileUrl} className={styles.image} />}
          {mediaType === 'video' && (
            <NativeVideoPlayer
              controls
              videoUrl={fileUrl}
              className={styles['video-player']}
              videoElementClassName={styles.video}
            />
          )}
        </div>
      )}
      <footer className={styles.footer}>
        <p className={styles.path}>
          <a href={fileUrl} target="_blank" className="text-ellipsis">
            <span className="text-ellipsis" title={fileUrl}>{fileUrl}</span>
          </a>
          {dimensions.width !== 0 && <span className={styles.dimensions}>{` ${dimensions.width}x${dimensions.height}`}</span>}
          {fileSize !== 0 && <span className={styles.filesize}>{` (${prettySize})`}</span>}
        </p>
      </footer>
    </StyledDialog>
  )
}
